import { getUriFilters } from '@webapp/account/src/resources/lib';
import type { Answer } from '@webapp/account/src/resources/reports/answers';
import type { ComparisonPeriod } from '@webapp/account/src/resources/reports/comparison';
import {
    ALL_TIME_PERIOD_ID,
    BASE_PERIOD_ID,
    NO_COMPARISON_TYPES,
} from '@webapp/account/src/resources/reports/comparison';
import type { GlobalFilter } from '@webapp/account/src/resources/reports/global-filters';
import type { Report } from '@webapp/account/src/resources/reports/reports';
import type { IWordStat } from '@webapp/account/src/resources/reports/word-stat';
import { selectMinFilterDate } from '@webapp/account/src/resources/survey/selectors';

import { today } from '@webapp/common/lib/date';
import type { SurveyAddonShare } from '@webapp/common/resources/entities/survey-addons/share';
import { QuestionType } from '@webapp/common/resources/survey';

import type { States } from '../states';

import type { ICrossTab, LinkStat, NearbyAnswers, ReportsState } from './state';

export const selectReports = ({ Reports }: States): ReportsState => Reports;

export const selectFilters = (states: States): GlobalFilter => {
    const r = selectReports(states);

    return r.globalFilters;
};

export const selectAnswer = (states: States): Answer => {
    const r = selectReports(states);

    return r.answer;
};

export const selectWordStatById =
    (id: number) =>
    (states: States): IWordStat => {
        const r = selectReports(states);

        return r.wordStat.find((item) => item.id === id);
    };

export const selectCrossTabsByAfterId =
    ({ afterQuestion, afterParam }: { afterQuestion?: number; afterParam?: number }) =>
    (states: States): Array<ICrossTab> => {
        const r = selectReports(states);

        return r.crossTabs
            .filter(
                (item) =>
                    (afterQuestion && item.after_question === afterQuestion) ||
                    (afterParam && item.after_link_param === afterParam),
            )
            .sort((a, b) => a.order - b.order);
    };

export const selectCrossTabStatById =
    (tabId: number) =>
    (states: States): Array<any> => {
        const r = selectReports(states);

        return r.crossTabsStat.get(tabId);
    };

export const selectCrossTabs = (states: States): Array<ICrossTab> => {
    const a = selectReports(states);

    return a.crossTabs;
};

export const selectFetchAnswer = (states: States): boolean => {
    const a = selectAnswer(states);

    return a.fetch;
};

export const selectNearbyAnswers = (states: States): NearbyAnswers => {
    const r = selectReports(states);

    return r.nearbyAnswers;
};

export const selectFetchPublic = (states: States): boolean => {
    const r = selectReports(states);

    return r.fetchPublic;
};

export const selectHideFilters = (states: States): boolean => {
    const r = selectReports(states);

    return r.hideFilters;
};

export const selectFilesSize = (states: States): number => {
    const r = selectReports(states);

    return r.filesSize;
};

export const selectFilesCount = (states: States): number => {
    const r = selectReports(states);

    return r.filesCount;
};

export const selectAudioSize = (states: States): number => {
    const r = selectReports(states);

    return r.audioSize;
};

export const selectAudioCount = (states: States): number => {
    const r = selectReports(states);

    return r.audioCount;
};

export const selectShareSettings = (states: States): SurveyAddonShare => {
    const r = selectReports(states);

    return r.share;
};

export const selectReportsQuestions = (states: States): Array<Report> => {
    const r = selectReports(states);

    return r.questions;
};

export const selectFiltersVisible = (states: States): boolean => {
    const r = selectReports(states);

    return r.filtersVisible;
};

export const selectFetchReports = (states: States): boolean => {
    const r = selectReports(states);

    return r.fetchReports;
};

export const selectResponsesLimit = (states: States): number => {
    const r = selectReports(states);

    return r.responsesLimit;
};

export const selectWithFilters = (states: States): boolean => {
    const f = selectFilters(states);
    const cd = selectMinFilterDate(states);

    return (
        f.filters.length > 0 ||
        Object.keys(f.tags).length > 0 ||
        f.dateFrom !== cd ||
        f.dateTo !== today ||
        (Array.isArray(f.comparePeriods) && f.comparePeriods.length > 0) ||
        f.linkParamsFilters.length > 0
    );
};

export const selectReportsList = (states: States): Array<Report> => {
    const r = selectReports(states);

    return r.reports;
};

export const selectWithFiles = (states: States): boolean => {
    const r = selectReports(states);

    return r.reports.some(({ type }) => type === QuestionType.FILE_UPLOAD);
};

export const selectStaleById =
    (id: number) =>
    (states: States): boolean => {
        const r = selectReports(states);

        return r.staleReports.has(id);
    };

export const selectLinkStats = (states: States): Map<number, LinkStat> => {
    const r = selectReports(states);

    return r.linkStats;
};

export const selectLinkStatsArray = (states: States): Array<LinkStat> => {
    const ls = selectLinkStats(states);

    return Array.from(ls, ([_, st]) => st);
};

export const selectLinkStatsById =
    (id: number) =>
    (states: States): LinkStat => {
        const ls = selectLinkStats(states);

        const formatted = Array.from(ls, ([_, st]) => st);

        return formatted.find((item) => item.id === id);
    };

export const selectLinkParamsFilters = (states: States): GlobalFilter['linkParamsFilters'] => {
    const gf = selectFilters(states);

    return gf.linkParamsFilters;
};

export const selectLinkParamFilters = (states: States): Array<{ sets: any; name: any; id: number }> => {
    const paramsFilters = selectLinkParamsFilters(states);
    const linkStats = selectLinkStats(states);

    return paramsFilters.map((f) => ({
        id: f.paramId,
        sets: [{ type: f.filterType, name: f.value, isDisabled: f.isDisabled }],
        name: linkStats.get(f.paramId)?.title || 'Удаленный параметр',
    }));
};

export const selectExcludedQuestions = (states: States): Set<number> => {
    const gf = selectFilters(states);

    return gf.excludedQuestionIds;
};

export const selectQuestionExcludedById =
    (id: number) =>
    (states: States): boolean => {
        const ex = selectExcludedQuestions(states);

        return ex.has(id);
    };

const selectEffectiveFilters = (states: States): GlobalFilter => getUriFilters() || selectFilters(states);

export const selectComparisonPeriods = (states: States): Array<ComparisonPeriod> => {
    const f = selectEffectiveFilters(states);
    const comparePeriods = Array.isArray(f.comparePeriods) ? f.comparePeriods : [];

    return [{ id: BASE_PERIOD_ID, dateFrom: f.dateFrom, dateTo: f.dateTo, name: f.basePeriodName }, ...comparePeriods];
};

export const selectComparisonEnabled = (states: States): boolean => selectComparisonPeriods(states).length > 1;

export const selectComparisonSeries =
    (reportId: number) =>
    (states: States): Array<{ period: ComparisonPeriod; report: Report | null }> => {
        const periods = selectComparisonPeriods(states);
        const baseReports = selectReportsList(states);
        const { byPeriod } = selectReports(states).comparison;

        return periods.map((period) => ({
            period,
            report:
                period.id === BASE_PERIOD_ID
                    ? baseReports.find(({ id }) => id === reportId) || null
                    : byPeriod[period.id]?.[reportId] || null,
        }));
    };

// Типы без сравнения грузятся отдельно «за всё время опроса».
export const selectComparisonAllTimeStale =
    (reportId: number) =>
    (states: States): boolean => {
        const question = selectReportsQuestions(states).find(({ id }) => id === reportId);

        if (!question || !NO_COMPARISON_TYPES.has(question.type)) {
            return false;
        }

        const { stale, byPeriod } = selectReports(states).comparison;

        return stale.has(`${ALL_TIME_PERIOD_ID}:${reportId}`) || !byPeriod[ALL_TIME_PERIOD_ID]?.[reportId];
    };

export const selectComparisonAllTimeReport =
    (reportId: number) =>
    (states: States): Report | null =>
        selectReports(states).comparison.byPeriod[ALL_TIME_PERIOD_ID]?.[reportId] || null;

export const selectComparisonStale =
    (reportId: number) =>
    (states: States): boolean => {
        const periods = selectComparisonPeriods(states).filter(({ id }) => id !== BASE_PERIOD_ID);
        const { stale, byPeriod } = selectReports(states).comparison;

        if (selectComparisonAllTimeStale(reportId)(states)) {
            return true;
        }

        // «stale» — после смены фильтров, «нет в byPeriod» — период ещё ни разу не грузился
        // (например, периоды восстановлены из сохранённой группы/сессии).
        return periods.some(({ id }) => stale.has(`${id}:${reportId}`) || !byPeriod[id]?.[reportId]);
    };

export const selectComparisonWordStatById =
    (periodId: string) =>
    (id: number) =>
    (states: States): IWordStat => {
        const { wordStatByPeriod } = selectReports(states).comparison;
        const list = wordStatByPeriod[periodId] || [];

        return list.find((item) => item.id === id);
    };
